<template>
  <div class="personal-data-form">
    <div class="personal-form">
      <div
        class="company-container"
        :class="{
          active: isCompany,
          'has-error': validation.errors.company,
        }"
      >
        <Checkbox
          v-if="!isCompany"
          :model-value="isCompany"
          class="company"
          @update:model-value="toggleIsCompany"
        >
          <span>{{ $t('ACCOUNT.PERSONAL.IS_COMPANY') }}</span>
        </Checkbox>

        <LabelInput v-if="isCompany">
          <Checkbox
            :model-value="isCompany"
            class="company-checkbox-inside-input"
            @update:model-value="toggleIsCompany"
          >
          </Checkbox>
          <label :class="{ disabled: !isCompany }" for="companyName">{{
            $t('ACCOUNT.PERSONAL.COMPANY')
          }}</label>
          <DebounceInput
            id="companyName"
            type="text"
            name="companyName"
            :model-value="data.company"
            :disabled="!isCompany"
            @update:model-value="updateCompanyName"
          />
          <AccountError
            v-if="validation.errors.company"
            :reason="validation.errors.company.reason"
          ></AccountError>
        </LabelInput>
      </div>

      <div class="full-name">
        <LabelInput :class="{ 'has-error': validation.errors.firstName }">
          <label for="firstName">{{ $t('ACCOUNT.PERSONAL.FIRSTNAME') }}</label>
          <DebounceInput
            id="firstName"
            type="text"
            :model-value="data.person.firstName"
            @update:model-value="updateFirstName"
          />
          <AccountError
            v-if="validation.errors.firstName"
            :reason="validation.errors.firstName.reason"
          ></AccountError>
        </LabelInput>

        <LabelInput :class="{ 'has-error': validation.errors.lastName }">
          <label for="lastName">{{ $t('ACCOUNT.PERSONAL.LASTNAME') }}</label>
          <DebounceInput
            id="lastName"
            type="text"
            :model-value="data.person.lastName"
            @update:model-value="updateLastName"
          />
          <AccountError
            v-if="validation.errors.lastName"
            :reason="validation.errors.lastName.reason"
          ></AccountError>
        </LabelInput>
      </div>
    </div>
    <div class="pdv">
      <div v-if="currentPdvState === PDV_STATES.FAILED" class="failed-address">
        <PdvFailedMsg :address="lastAddress"></PdvFailedMsg>
      </div>
      <div
        v-if="currentPdvState === PDV_STATES.CODE_INPUT && !manualAddressEdit"
        class="hc-code-input"
      >
        <h3 v-html="$t('PDV.HARDCHECK.HEADING')"></h3>
        <p
          v-if="!showCodeErrorMsg && pdvState.hardcheck.attemptsLeft < 5"
          v-html="
            $t('PDV.ATTEMPTS_LEFT', {
              count: pdvState.hardcheck.attemptsLeft,
            })
          "
        ></p>
        <div v-if="showCodeErrorMsg" class="hc-failed-msg">
          <div class="symbol">!</div>
          <div>
            {{ $t('PDV.HARDCHECK.FAILED') }}
            <span
              class="attempts"
              v-html="
                $t('PDV.ATTEMPTS_LEFT', {
                  count: pdvState.hardcheck.attemptsLeft,
                })
              "
            ></span>
          </div>
        </div>

        <div class="code-input">
          <LabelInput>
            <label
              for="hc-code"
              v-html="$t('PDV.HARDCHECK.INPUT_LABEL')"
            ></label>
            <input id="hc-code" v-model="hcCode" type="text" />
          </LabelInput>
          <ProgressButton
            class="btn btn-primary btn-xl icon-btn"
            :on-click="confirmCode"
            :disabled="lastHcCode === hcCode"
            label="GENERAL.YES"
          />
        </div>
        <PdvExistingAddress
          :data="data"
          :pdv-state="pdvState"
          @edit-address="onEditAddress"
        ></PdvExistingAddress>
      </div>
      <div
        v-if="currentPdvState === PDV_STATES.VALID && !manualAddressEdit"
        class="verified-address"
      >
        <PdvExistingAddress
          :data="data"
          :pdv-state="pdvState"
          @edit-address="onEditAddress"
        ></PdvExistingAddress>
      </div>
      <div
        v-show="
          [PDV_STATES.ADDRESS_INPUT, PDV_STATES.DEACTIVATED].includes(
            currentPdvState,
          ) || manualAddressEdit
        "
        class="address-form"
        :class="{
          'sc-failed': softCheckFailed,
          'pdv-deactivated': currentPdvState === PDV_STATES.DEACTIVATED,
        }"
      >
        <div v-if="!hidePdvValidateMsg" class="validate-msg">
          <p v-html="$t('PDV.VALIDATE_INFO')"></p>
          <p
            v-if="pdvState.softcheck.attemptsLeft < 5"
            class="attempts"
            v-html="
              $t('PDV.ATTEMPTS_LEFT', {
                count: pdvState.softcheck.attemptsLeft,
              })
            "
          ></p>
        </div>
        <div class="sc-failed-msg">
          <div class="symbol">!</div>
          <div>
            {{ $t('PDV.VALIDATION_FAILED') }}
            <span
              v-if="pdvState.softcheck.attemptsLeft < 5"
              class="attempts"
              v-html="
                $t('PDV.ATTEMPTS_LEFT', {
                  count: pdvState.softcheck.attemptsLeft,
                })
              "
            ></span>
            <SmartFaqLink class="sc-failed-help-link" :link="pdvHelpLink" />
          </div>
        </div>
        <div class="country columns">
          <div :class="{ 'has-error': validation.errors.country }">
            <LabelInput>
              <label>{{ $t('ACCOUNT.PERSONAL.COUNTRY') }}</label>
              <Dropdown
                :items="sortedCountries"
                :item-key="(country) => country.countryCode"
                :item-to-string="(country) => country.name"
                :value="currentCountry"
                @change="setCountry"
              >
              </Dropdown>
              <AccountError
                v-if="validation.errors.country"
                :reason="validation.errors.country.reason"
              ></AccountError>
            </LabelInput>
          </div>

          <div
            v-if="currentCountry?.states?.length"
            :class="{ 'has-error': validation.errors.state }"
          >
            <LabelInput>
              <label>{{ $t('ACCOUNT.PERSONAL.STATE') }}</label>
              <Dropdown
                :items="currentCountry.states"
                :item-key="(state) => state.code"
                :item-to-string="(state) => state.value"
                :value="currentState"
                @change="setState"
              >
              </Dropdown>
              <AccountError
                v-if="validation.errors.state"
                :reason="validation.errors.state.reason"
              ></AccountError>
            </LabelInput>
          </div>
        </div>
        <LabelInput :class="{ 'has-error': validation.errors.street }">
          <label for="street">{{ $t('ACCOUNT.PERSONAL.STREET') }}</label>
          <DebounceInput
            id="street"
            type="text"
            :model-value="data.street"
            placeholder=""
            @update:model-value="updateStreet"
          />
          <AccountError
            v-if="validation.errors.street"
            :reason="validation.errors.street.reason"
          ></AccountError>
        </LabelInput>
        <LabelInput
          :class="{ 'has-error': validation.errors.street2 }"
          :model="data.streetAnnex"
        >
          <label for="street2">{{ $t('ACCOUNT.PERSONAL.STREET2') }}</label>
          <DebounceInput
            id="street2"
            ref="street2"
            type="text"
            :model-value="data.streetAnnex"
            @update:model-value="updateStreet2"
          />
          <AccountError
            v-if="validation.errors.street2"
            :reason="validation.errors.street2.reason"
          ></AccountError>
        </LabelInput>

        <div class="state">
          <LabelInput
            class="postal-code"
            :class="{ 'has-error': validation.errors.postalCode }"
            :model="data.zipCode"
          >
            <label for="zipCode">{{
              $t('ACCOUNT.PERSONAL.POSTAL_CODE')
            }}</label>
            <DebounceInput
              id="zipCode"
              ref="zipCode"
              type="text"
              :model-value="data.zipCode"
              @update:model-value="updatePostalCode"
            />
            <AccountError
              v-if="validation.errors.postalCode"
              :reason="validation.errors.postalCode.reason"
            ></AccountError>
          </LabelInput>
          <LabelInput
            class="city"
            :class="{ 'has-error': validation.errors.city }"
            :model="data.city"
          >
            <label for="city">{{ $t('ACCOUNT.PERSONAL.CITY') }}</label>
            <DebounceInput
              id="city"
              ref="city"
              type="text"
              :model-value="data.city"
              @update:model-value="updateCity"
            />
            <AccountError
              v-if="validation.errors.city"
              :reason="validation.errors.city.reason"
            ></AccountError>
          </LabelInput>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { mapActions, mapState } from 'pinia';
import pdvService from 'commons/partnerDataValidation/service/pdvService';

import DebounceInput from 'src/app/components/input/DebounceInput.vue';
import pdvAutoComplete from 'commons/partnerDataValidation/autoComplete/pdvAutoComplete';
import LabelInput from 'commons/labelInput/LabelInput.vue';
import AccountError from '../errorMessage/AccountError.vue';
import Dropdown from 'commons/Dropdown/Dropdown.vue';
import Checkbox from 'commons/checkbox/Checkbox.vue';
import PdvFailedMsg from 'commons/partnerDataValidation/failedMsg/PdvFailedMsg.vue';
import PdvExistingAddress from 'commons/partnerDataValidation/existingAddress/PdvExistingAddress.vue';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import SmartFaqLink from 'commons/helpLink/SmartFaqLink.vue';
import smartFaqLinks from 'commons/smartFaq/smartFaqLinks';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import PdvFailedDialog from 'commons/partnerDataValidation/failedDialog/PdvFailedDialog.vue';
import userCoreService from 'commons/api/userAccount/userCoreService';
import { useUserAddress } from 'commons/stores/user/userAddress';

export default {
  name: 'PersonalDataForm',
  components: {
    LabelInput,
    AccountError,
    Dropdown,
    Checkbox,
    PdvFailedMsg,
    ProgressButton,
    PdvExistingAddress,
    SmartFaqLink,
    DebounceInput,
  },
  props: {
    updatePdvState: {
      type: Function,
    },
    hidePdvValidateMsg: {
      type: Boolean,
    },
    skipPdvFailedDialog: {
      type: Boolean,
    },
  },
  emits: ['onEditAddress'],
  data() {
    return {
      currentCountry: null,
      currentState: null,
      PDV_STATES: pdvService.STATES,
      showCodeErrorMsg: false,
      hcCode: '',
      lastHcCode: '',
      manualAddressEdit: false,
      pdvHelpLink: smartFaqLinks.PDV_INFO,
      autoCompleteUpdater: null,
    };
  },
  computed: {
    ...mapState(useUserAddress, [
      'data',
      'countries',
      'validation',
      'pdvState',
      'isCompany',
    ]),
    sortedCountries() {
      return [...this.countries].sort((a, b) => a.name.localeCompare(b.name));
    },
    currentPdvState() {
      return pdvService.getCurrentState(this.pdvState);
    },
    softCheckFailed() {
      return pdvService.softCheckFailed(this.pdvState);
    },
    lastAddress() {
      return {
        street: this.data.street,
        postalCode: this.data.zipCode,
        city: this.data.city,
        country: this.countries.find(
          (county) => county.countryCode === this.data.country?.code,
        ).name,
        countryCode: this.data.country?.code,
      };
    },
  },
  watch: {
    pdvState: function (newState, oldState) {
      if (
        newState &&
        [
          pdvService.STATES.VALID,
          pdvService.STATES.FAILED,
          pdvService.STATES.CODE_INPUT,
        ].includes(this.currentPdvState)
      ) {
        this.manualAddressEdit = false;
      }

      if (
        pdvService.getCurrentState(oldState) !==
          pdvService.getCurrentState(newState) &&
        this.currentPdvState === pdvService.STATES.FAILED
      ) {
        if (!this.skipPdvFailedDialog) {
          dialogService.openDialog(PdvFailedDialog, {
            lastAddress: this.lastAddress,
          });
        }
      }
    },
  },
  created() {
    if (this.data.country?.code) {
      this.currentCountry = this.countries.find(
        (c) => c.countryCode === this.data.country?.code,
      );
      this.currentState = this.currentCountry.states?.length
        ? this.currentCountry.states.find(
            (state) => state.code === this.data.state?.code,
          )
        : null;
    }
  },
  mounted() {
    pdvAutoComplete
      .init({
        elementId: 'street',
        countryId: this.data.country?.code,
        onPlaceChange: (placeData) => {
          const placeCountry = this.countries.find(
            (country) => country.countryCode === placeData.country,
          );
          if (!this.currentCountry && placeCountry) {
            this.setCountry(placeCountry);
          }
          this.updateStreet(placeData.name);
          document.getElementById('street').value = placeData.name;

          this.updateStreet2(placeData.street2);
          this.updatePostalCode(placeData.postalCode);
          this.updateCity(placeData.city);
        },
      })
      .then((updater) => {
        this.autoCompleteUpdater = updater;
      });
  },
  methods: {
    ...mapActions(useUserAddress, {
      updateCompanyName: 'setCompanyName',
      deleteCompanyName: 'deleteCompanyName',
      updateLastName: 'setLastName',
      updateFirstName: 'setFirstName',
      updateCountry: 'setCountry',
      updateState: 'setState',
      updateStreet: 'setStreet',
      updateStreet2: 'setStreet2',
      updatePostalCode: 'setPostalCode',
      updateCity: 'setCity',
    }),
    toggleIsCompany() {
      if (this.isCompany) {
        this.deleteCompanyName();
      } else {
        this.updateCompanyName('');
      }
    },
    setCountry(country) {
      this.currentCountry = country;
      this.updateCountry(country);
      this.setState(null);
      if (this.autoCompleteUpdater) {
        this.autoCompleteUpdater.changeCountry(country.countryCode);
      }
    },
    setState(state) {
      this.currentState = state;
      this.updateState(state);
    },
    confirmCode() {
      this.lastHcCode = this.hcCode;

      return pdvService.validateCode(this.hcCode).then(
        () => {
          this.updatePdvState();
        },
        () => {
          this.updatePdvState().then(() => {
            this.showCodeErrorMsg = true;
          });
        },
      );
    },
    onEditAddress() {
      this.manualAddressEdit = true;
      this.$emit('onEditAddress');
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/_type.scss';

.labelInput,
.radiobutton-group,
.company-container {
  margin-bottom: 16px;
}

.code-input {
  .labelInput {
    margin-bottom: 0;
    flex-grow: 1;
    margin-right: 10px;
  }
}

.company-container {
  .company {
    padding-top: 12px;
    display: flex;
    align-items: center;

    & > label {
      margin-left: 8px;
    }
  }

  .label-input-wrapper {
    position: relative;
    display: flex;

    .company-checkbox-inside-input {
      position: absolute;
      left: 10px !important;
      top: 10px !important;
      padding: 0;
    }

    & > label {
      left: 40px;
    }

    & > input {
      padding-left: 44px;
    }
  }
}

.has-error .radio-button {
  border-color: $pa-color-red;
}

.personal-data-form {
  margin-bottom: -16px;
  max-width: 600px;
}

.personal-data-form,
.full-name {
  display: flex;
  flex-wrap: wrap;

  & > div {
    min-width: 350px;
    flex: 1;
  }
}

.full-name {
  & > div {
    min-width: 200px;

    &:first-child {
      margin-right: 24px;
    }
  }
}

.state {
  display: flex;

  .postal-code {
    width: 120px;
    margin-right: 10px;
  }

  .city {
    flex-grow: 1;
  }
}

.pdv {
  display: flex;
  flex-direction: column;
}

.sc-failed-msg,
.hc-failed-msg {
  display: flex;
  background-color: $pa-color-red;
  color: #fff;
  align-items: center;
  padding: 10px;
  margin-bottom: 15px;

  .symbol {
    font-size: 2em;
    padding: 0 20px 0 10px;
  }

  .attempts {
    font-weight: bold;
  }

  .sc-failed-help-link {
    color: white;
    font-weight: bold;
    margin-top: 4px;

    .icon {
      margin-bottom: 1px;
    }
  }
}

.address-form {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  .validate-msg {
    margin-bottom: 15px;
    text-align: right;

    p {
      margin: 0;
      display: inline-block;

      @extend .text-sm;
    }

    .attempts {
      color: $pa-color-main;
    }
  }

  .sc-failed-msg {
    display: none;
  }

  &.sc-failed {
    .sc-failed-msg {
      display: flex;
    }

    .validate-msg {
      display: none;
    }
  }

  &.pdv-deactivated {
    .validate-msg {
      display: none;
    }
  }
}

.hc-code-input {
  padding-bottom: 20px;

  .code-input {
    display: flex;
    margin: 10px 0;
  }

  & > h3 {
    text-align: center;
    margin: 0 0 5px 0;
  }

  & > p {
    text-align: center;
    margin: 0;
    color: $sprd-color-medium-grey;
  }
}

.verified-address {
  margin-bottom: 16px;
}

.failed-address {
  .pdv-failed-msg {
    background-color: $sprd-color-lightest-grey;
    padding: 15px;
    margin-bottom: 15px;

    h1 {
      @extend .heading3;
    }

    .main-icon {
      width: 30px;
      height: 30px;
    }

    .icon-wrapper {
      padding: 10px;

      .icon {
        width: 20px !important;
        height: 20px !important;
      }
    }

    .icon-outer-wrapper {
      top: -20px;
    }

    .or {
      margin: 10px 0;
    }
  }
}
</style>
